import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { productsAPI, materialsAPI, uploadAPI, userSettingsAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { ComboboxWithAdd } from '../components/ui/combobox-with-add';
import { toast } from 'sonner';
import { 
  Plus, 
  Search, 
  Package, 
  Pencil, 
  Trash2, 
  DollarSign,
  Clock,
  Sparkles,
  X,
  Upload,
  Image as ImageIcon,
  ChevronRight,
  AlertCircle,
  Settings2
} from 'lucide-react';
import { HelpButton } from '../components/HelpButton';
import { SafeImg } from '../components/SafeImg';

const SKILL_LEVELS = [
  { value: 'beginner', label: 'Beginner (1.0x)', multiplier: 1.0 },
  { value: 'intermediate', label: 'Intermediate (1.15x)', multiplier: 1.15 },
  { value: 'advanced', label: 'Advanced (1.35x)', multiplier: 1.35 },
  { value: 'master', label: 'Master (1.6x)', multiplier: 1.6 },
];

const DEFAULT_CATEGORIES = [
  'jewelry', 'pottery', 'textiles', 'woodwork', 'candles', 
  'soap', 'art', 'leather', 'glass', 'paper', 'food', 'other'
];

export const Products = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [categories, setCategories] = useState(DEFAULT_CATEGORIES);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isPricingOpen, setIsPricingOpen] = useState(false);
  const [isCategoriesOpen, setIsCategoriesOpen] = useState(false);
  const [pricingLoading, setPricingLoading] = useState(false);
  const [pricingData, setPricingData] = useState(null);
  const [editingProduct, setEditingProduct] = useState(null);
  const [imageUploading, setImageUploading] = useState(false);
  const [newCategoryInput, setNewCategoryInput] = useState('');
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    sku: '',
    category: '',
    image_url: '',
    labor_minutes: '',
    skill_level: 'intermediate',
    retail_price: '',
    wholesale_price: '',
    materials: [],
    notes: '',
  });

  const [bomItem, setBomItem] = useState({ material_id: '', quantity: '' });

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchData();
  }, [user, navigate]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [productsRes, materialsRes, categoriesRes] = await Promise.all([
        productsAPI.getAll(),
        materialsAPI.getAll(),
        userSettingsAPI.get('product_categories')
      ]);
      setProducts(productsRes.data);
      setMaterials(materialsRes.data);
      setCategories(categoriesRes.data.values || DEFAULT_CATEGORIES);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleAddCategory = async (newCategory) => {
    try {
      const response = await userSettingsAPI.addValue('product_categories', newCategory);
      setCategories(response.data.values);
      toast.success(`Category "${newCategory}" added`);
    } catch (error) {
      toast.error('Failed to add category');
    }
  };

  const handleRemoveCategory = async (categoryToRemove) => {
    // Check if category is in use
    const inUse = products.some(p => p.category === categoryToRemove);
    if (inUse) {
      toast.error(`Cannot remove "${categoryToRemove}" - it's being used by products`);
      return;
    }
    try {
      const response = await userSettingsAPI.removeValue('product_categories', categoryToRemove);
      setCategories(response.data.values);
      toast.success(`Category "${categoryToRemove}" removed`);
    } catch (error) {
      toast.error('Failed to remove category');
    }
  };

  const handleAddNewCategoryFromDialog = async () => {
    if (!newCategoryInput.trim()) return;
    await handleAddCategory(newCategoryInput.trim());
    setNewCategoryInput('');
  };

  const handleOpenDialog = (product = null) => {
    if (product) {
      setEditingProduct(product);
      setFormData({
        name: product.name || '',
        description: product.description || '',
        sku: product.sku || '',
        category: product.category || '',
        image_url: product.image_url || '',
        labor_minutes: product.labor_minutes?.toString() || '',
        skill_level: product.skill_level || 'intermediate',
        retail_price: product.retail_price?.toString() || '',
        wholesale_price: product.wholesale_price?.toString() || '',
        materials: product.materials?.map(m => ({
          material_id: m.material_id,
          quantity: m.quantity
        })) || [],
        notes: product.notes || '',
      });
    } else {
      setEditingProduct(null);
      setFormData({
        name: '',
        description: '',
        sku: '',
        category: '',
        image_url: '',
        labor_minutes: '',
        skill_level: 'intermediate',
        retail_price: '',
        wholesale_price: '',
        materials: [],
        notes: '',
      });
    }
    setIsDialogOpen(true);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setImageUploading(true);
    try {
      const response = await uploadAPI.uploadImage(file);
      setFormData({...formData, image_url: response.data.url});
      toast.success('Image uploaded');
    } catch (error) {
      toast.error('Failed to upload image');
    } finally {
      setImageUploading(false);
    }
  };

  const handleAddBomItem = () => {
    if (!bomItem.material_id || !bomItem.quantity) {
      toast.error('Select a material and enter quantity');
      return;
    }
    
    // Check if material already exists
    if (formData.materials.some(m => m.material_id === bomItem.material_id)) {
      toast.error('Material already added');
      return;
    }

    setFormData({
      ...formData,
      materials: [...formData.materials, {
        material_id: bomItem.material_id,
        quantity: parseFloat(bomItem.quantity)
      }]
    });
    setBomItem({ material_id: '', quantity: '' });
  };

  const handleRemoveBomItem = (materialId) => {
    setFormData({
      ...formData,
      materials: formData.materials.filter(m => m.material_id !== materialId)
    });
  };

  const getMaterialById = (id) => materials.find(m => m.id === id);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.category) {
      toast.error('Name and category are required');
      return;
    }

    const payload = {
      name: formData.name,
      description: formData.description || null,
      sku: formData.sku || null,
      category: formData.category,
      image_url: formData.image_url || null,
      labor_minutes: formData.labor_minutes ? parseFloat(formData.labor_minutes) : 0,
      skill_level: formData.skill_level,
      retail_price: formData.retail_price ? parseFloat(formData.retail_price) : null,
      wholesale_price: formData.wholesale_price ? parseFloat(formData.wholesale_price) : null,
      materials: formData.materials,
      notes: formData.notes || null,
    };

    try {
      if (editingProduct) {
        await productsAPI.update(editingProduct.id, payload);
        toast.success('Product updated');
      } else {
        await productsAPI.create(payload);
        toast.success('Product created');
      }
      setIsDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save product');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    
    try {
      await productsAPI.delete(id);
      toast.success('Product deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete product');
    }
  };

  const handleGetPricing = async (product) => {
    setPricingData(null);
    setPricingLoading(true);
    setIsPricingOpen(true);
    
    try {
      // Convert labor_minutes to hours for API call
      const laborHours = (product.labor_minutes || 0) / 60 || 1;
      
      const response = await productsAPI.getPricing({
        product_name: product.name,
        materials_cost: product.material_cost || 0,
        labor_hours: laborHours,
        category: product.category,
        description: product.description
      });
      
      // Map the response to expected field names and add product info
      const data = response.data;
      setPricingData({
        product_id: product.id,
        product_name: product.name,
        material_cost: data.materials_cost || 0,
        labor_cost: data.labor_cost || 0,
        total_cost: data.total_cost || 0,
        recommended_retail_price: data.retail_price || 0,
        recommended_wholesale_price: data.wholesale_price || 0,
        craft_show_price: data.craft_show_price || 0,
        online_price: data.online_price || 0,
        profit_margin: data.margin_percent || 0,
        reasoning: data.pricing_rationale || '',
        market_comparison: data.market_comparison || '',
        pricing_tiers: [
          { tier: 'craft_show', description: 'For craft shows and local markets', price: data.craft_show_price || 0 },
          { tier: 'online', description: 'For online sales (Etsy, website)', price: data.online_price || 0 }
        ]
      });
    } catch (error) {
      console.error('Pricing error:', error);
      toast.error('Failed to get pricing recommendation');
      setIsPricingOpen(false);
    } finally {
      setPricingLoading(false);
    }
  };

  const handleApplyPricing = async (retailPrice, wholesalePrice) => {
    if (!pricingData) return;
    
    try {
      await productsAPI.update(pricingData.product_id, {
        retail_price: retailPrice,
        wholesale_price: wholesalePrice
      });
      toast.success('Prices updated');
      setIsPricingOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to update prices');
    }
  };

  const filteredProducts = products.filter(p => {
    const matchesSearch = p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.sku?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.description?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = !categoryFilter || p.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  if (!user) return null;

  return (
    <div className="min-h-screen bg-[#FFFFFF] py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <div className="flex items-center gap-2">
            <div>
              <h1 className="text-3xl font-bold text-[#1E3A5F] font-['Outfit']">Product Catalog</h1>
              <p className="text-gray-600 mt-1">Manage products, costs, and pricing</p>
            </div>
            <HelpButton tutorialId="products" />
          </div>
          <div className="flex gap-3 flex-wrap">
            <Button 
              variant="outline" 
              className="border-2 border-[#1E3A5F]"
              onClick={() => setIsCategoriesOpen(true)}
              data-testid="manage-categories-btn"
            >
              <Settings2 className="w-4 h-4 mr-2" />
              Categories
            </Button>
            <Link to="/materials">
              <Button variant="outline" className="border-2 border-[#1E3A5F]">
                <Package className="w-4 h-4 mr-2" />
                Materials
              </Button>
            </Link>
            <Button 
              onClick={() => handleOpenDialog()} 
              className="btn-primary"
              data-testid="add-product-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Product
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl border-2 border-[#1E3A5F] p-4 mb-6 shadow-hard">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search products..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="search-products"
              />
            </div>
            <Select value={categoryFilter || "all"} onValueChange={(val) => setCategoryFilter(val === "all" ? "" : val)}>
              <SelectTrigger className="w-full sm:w-48" data-testid="category-filter">
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.filter(cat => cat).map((cat) => (
                  <SelectItem key={cat} value={cat} className="capitalize">
                    {cat}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Products Grid */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1E3A5F]"></div>
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="bg-white rounded-xl border-2 border-[#1E3A5F] p-12 text-center shadow-hard">
            <Package className="w-16 h-16 mx-auto text-gray-300 mb-4" />
            <h3 className="text-xl font-semibold text-[#1E3A5F] mb-2">No products found</h3>
            <p className="text-gray-600 mb-4">
              {searchQuery || categoryFilter 
                ? "Try adjusting your filters" 
                : "Start by adding your first product"
              }
            </p>
            {!searchQuery && !categoryFilter && (
              <Button onClick={() => handleOpenDialog()} className="btn-primary">
                <Plus className="w-4 h-4 mr-2" />
                Add Product
              </Button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredProducts.map((product) => (
              <div
                key={product.id}
                className="bg-white rounded-xl border-2 border-[#1E3A5F] overflow-hidden shadow-hard hover:shadow-hard-hover transition-all"
                data-testid={`product-card-${product.id}`}
              >
                {/* Product Image */}
                <div className="h-40 bg-gray-100 relative">
                  <SafeImg
                    src={
                      product.image_url
                        ? (product.image_url.startsWith('/')
                            ? `${process.env.REACT_APP_BACKEND_URL}${product.image_url}`
                            : product.image_url)
                        : null
                    }
                    alt={product.name}
                    className="w-full h-full object-cover"
                    fallback={
                      <div className="w-full h-full flex flex-col items-center justify-center text-gray-400">
                        <ImageIcon className="w-12 h-12 text-gray-300" />
                        {product.image_url && (
                          <span className="text-xs mt-2 px-3 text-center" data-testid={`product-image-missing-${product.id}`}>
                            Image unavailable — re-upload to restore
                          </span>
                        )}
                      </div>
                    }
                  />
                  <span className="absolute top-2 right-2 px-2 py-1 bg-[#1E3A5F] text-white text-xs rounded-full capitalize">
                    {product.category}
                  </span>
                </div>

                {/* Product Info */}
                <div className="p-4">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <h3 className="font-semibold text-[#1E3A5F] text-lg">{product.name}</h3>
                      {product.sku && (
                        <p className="text-xs text-gray-500">SKU: {product.sku}</p>
                      )}
                    </div>
                  </div>

                  {/* Cost Breakdown */}
                  <div className="grid grid-cols-3 gap-2 text-sm mb-3">
                    <div className="text-center p-2 bg-gray-50 rounded">
                      <p className="text-gray-500 text-xs">Materials</p>
                      <p className="font-mono font-semibold">${product.material_cost?.toFixed(2) || '0.00'}</p>
                    </div>
                    <div className="text-center p-2 bg-gray-50 rounded">
                      <p className="text-gray-500 text-xs">Labor</p>
                      <p className="font-mono font-semibold">${product.labor_cost?.toFixed(2) || '0.00'}</p>
                    </div>
                    <div className="text-center p-2 bg-[#FFEDDF] rounded">
                      <p className="text-gray-500 text-xs">Total Cost</p>
                      <p className="font-mono font-semibold text-[#1E3A5F]">${product.total_cost?.toFixed(2) || '0.00'}</p>
                    </div>
                  </div>

                  {/* Prices */}
                  <div className="flex justify-between items-center mb-3">
                    <div>
                      <p className="text-xs text-gray-500">Retail</p>
                      <p className="font-bold text-[#1E3A5F] text-lg">
                        {product.retail_price ? `$${product.retail_price.toFixed(2)}` : '-'}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-gray-500">Wholesale</p>
                      <p className="font-semibold text-gray-700">
                        {product.wholesale_price ? `$${product.wholesale_price.toFixed(2)}` : '-'}
                      </p>
                    </div>
                  </div>

                  {/* Labor & Skill */}
                  <div className="flex items-center gap-4 text-sm text-gray-600 mb-4">
                    <span className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {product.labor_minutes} min
                    </span>
                    <span className="capitalize">{product.skill_level}</span>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleGetPricing(product)}
                      className="flex-1 border-[#1E3A5F] text-[#1E3A5F] hover:bg-[#1E3A5F] hover:text-white"
                      data-testid={`pricing-btn-${product.id}`}
                    >
                      <Sparkles className="w-4 h-4 mr-1" />
                      AI Price
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleOpenDialog(product)}
                      data-testid={`edit-product-${product.id}`}
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(product.id)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      data-testid={`delete-product-${product.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Summary Stats */}
        {products.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mt-8">
            <div className="bg-white rounded-xl border-2 border-[#1E3A5F] p-4 shadow-hard">
              <p className="text-sm text-gray-600">Total Products</p>
              <p className="text-2xl font-bold text-[#1E3A5F]">{products.length}</p>
            </div>
            <div className="bg-white rounded-xl border-2 border-[#1E3A5F] p-4 shadow-hard">
              <p className="text-sm text-gray-600">Categories</p>
              <p className="text-2xl font-bold text-[#1E3A5F]">
                {new Set(products.map(p => p.category)).size}
              </p>
            </div>
            <div className="bg-white rounded-xl border-2 border-[#1E3A5F] p-4 shadow-hard">
              <p className="text-sm text-gray-600">Avg. Cost</p>
              <p className="text-2xl font-bold text-[#1E3A5F]">
                ${(products.reduce((sum, p) => sum + (p.total_cost || 0), 0) / products.length).toFixed(2)}
              </p>
            </div>
            <div className="bg-white rounded-xl border-2 border-[#1E3A5F] p-4 shadow-hard">
              <p className="text-sm text-gray-600">Priced Products</p>
              <p className="text-2xl font-bold text-green-600">
                {products.filter(p => p.retail_price).length}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Add/Edit Product Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-2xl border-2 border-[#1E3A5F] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-[#1E3A5F]">
              {editingProduct ? 'Edit Product' : 'Add Product'}
            </DialogTitle>
            <DialogDescription>
              {editingProduct 
                ? 'Update product details and bill of materials' 
                : 'Create a new product with materials and labor tracking'
              }
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2 sm:col-span-1">
                <Label htmlFor="name">Product Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="e.g., Beaded Bracelet"
                  required
                  data-testid="product-name-input"
                />
              </div>
              
              <div className="col-span-2 sm:col-span-1">
                <Label htmlFor="sku">SKU</Label>
                <Input
                  id="sku"
                  value={formData.sku}
                  onChange={(e) => setFormData({...formData, sku: e.target.value})}
                  placeholder="e.g., BB-001"
                  data-testid="product-sku-input"
                />
              </div>
              
              <div className="col-span-2 sm:col-span-1">
                <Label htmlFor="category">Category *</Label>
                <ComboboxWithAdd
                  options={categories}
                  value={formData.category}
                  onValueChange={(value) => setFormData({...formData, category: value})}
                  onAddNew={handleAddCategory}
                  placeholder="Select category"
                  searchPlaceholder="Search or add category..."
                  addNewText="Add category"
                  data-testid="product-category-select"
                />
              </div>
              
              <div className="col-span-2 sm:col-span-1">
                <Label htmlFor="skill_level">Skill Level</Label>
                <Select
                  value={formData.skill_level}
                  onValueChange={(value) => setFormData({...formData, skill_level: value})}
                >
                  <SelectTrigger data-testid="product-skill-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SKILL_LEVELS.map((level) => (
                      <SelectItem key={level.value} value={level.value}>
                        {level.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="col-span-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="Product description..."
                  rows={2}
                  data-testid="product-description-input"
                />
              </div>
            </div>

            {/* Image Upload */}
            <div>
              <Label>Product Image</Label>
              <div className="flex items-center gap-4 mt-2">
                {formData.image_url ? (
                  <div className="relative w-20 h-20 rounded-lg overflow-hidden border-2 border-[#1E3A5F]">
                    <SafeImg
                      src={formData.image_url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${formData.image_url}` : formData.image_url}
                      alt="Product"
                      className="w-full h-full object-cover"
                      fallback={
                        <div className="w-full h-full flex items-center justify-center bg-gray-100 text-gray-400">
                          <ImageIcon className="w-6 h-6" />
                        </div>
                      }
                    />
                    <button
                      type="button"
                      onClick={() => setFormData({...formData, image_url: ''})}
                      className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ) : (
                  <label className="w-20 h-20 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center cursor-pointer hover:border-[#1E3A5F] transition-colors">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                      disabled={imageUploading}
                    />
                    {imageUploading ? (
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#1E3A5F]"></div>
                    ) : (
                      <Upload className="w-6 h-6 text-gray-400" />
                    )}
                  </label>
                )}
                <div className="text-sm text-gray-500">
                  Upload a product image (max 5MB)
                </div>
              </div>
            </div>

            {/* Labor */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="labor_minutes">Labor Time (minutes)</Label>
                <Input
                  id="labor_minutes"
                  type="number"
                  min="0"
                  value={formData.labor_minutes}
                  onChange={(e) => setFormData({...formData, labor_minutes: e.target.value})}
                  placeholder="0"
                  data-testid="product-labor-input"
                />
              </div>
              <div className="flex items-end">
                <p className="text-sm text-gray-500">
                  Labor cost: ${formData.labor_minutes ? (
                    (parseFloat(formData.labor_minutes) / 60) * 15 * 
                    (SKILL_LEVELS.find(s => s.value === formData.skill_level)?.multiplier || 1)
                  ).toFixed(2) : '0.00'} (at $15/hr)
                </p>
              </div>
            </div>

            {/* Bill of Materials */}
            <div>
              <Label className="text-lg font-semibold text-[#1E3A5F]">Bill of Materials</Label>
              <p className="text-sm text-gray-500 mb-3">Add materials needed to create this product</p>
              
              {/* Add BOM Item */}
              <div className="flex gap-2 mb-3">
                <Select
                  value={bomItem.material_id || undefined}
                  onValueChange={(value) => setBomItem({...bomItem, material_id: value})}
                >
                  <SelectTrigger className="flex-1" data-testid="bom-material-select">
                    <SelectValue placeholder="Select material" />
                  </SelectTrigger>
                  <SelectContent>
                    {materials.map((mat) => (
                      <SelectItem key={mat.id} value={mat.id}>
                        {mat.name} (${mat.cost_per_unit}/{mat.unit_type})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={bomItem.quantity}
                  onChange={(e) => setBomItem({...bomItem, quantity: e.target.value})}
                  placeholder="Qty"
                  className="w-24"
                  data-testid="bom-quantity-input"
                />
                <Button type="button" onClick={handleAddBomItem} variant="outline" data-testid="add-bom-btn">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>

              {/* BOM List */}
              {formData.materials.length > 0 ? (
                <div className="border rounded-lg divide-y">
                  {formData.materials.map((item) => {
                    const material = getMaterialById(item.material_id);
                    if (!material) return null;
                    const itemCost = material.cost_per_unit * item.quantity;
                    return (
                      <div key={item.material_id} className="flex items-center justify-between p-2">
                        <div>
                          <p className="font-medium">{material.name}</p>
                          <p className="text-sm text-gray-500">
                            {item.quantity} {material.unit_type} @ ${material.cost_per_unit}/{material.unit_type}
                          </p>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="font-mono font-semibold">${itemCost.toFixed(2)}</span>
                          <button
                            type="button"
                            onClick={() => handleRemoveBomItem(item.material_id)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                  <div className="flex justify-between p-2 bg-[#FFEDDF]">
                    <span className="font-semibold">Total Material Cost</span>
                    <span className="font-mono font-bold text-[#1E3A5F]">
                      ${formData.materials.reduce((sum, item) => {
                        const mat = getMaterialById(item.material_id);
                        return sum + (mat ? mat.cost_per_unit * item.quantity : 0);
                      }, 0).toFixed(2)}
                    </span>
                  </div>
                </div>
              ) : (
                <div className="text-center py-4 border rounded-lg bg-gray-50">
                  <p className="text-gray-500">No materials added yet</p>
                  {materials.length === 0 && (
                    <Link to="/materials" className="text-[#1E3A5F] hover:underline text-sm">
                      Add materials first →
                    </Link>
                  )}
                </div>
              )}
            </div>

            {/* Pricing */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="retail_price">Retail Price</Label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    id="retail_price"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.retail_price}
                    onChange={(e) => setFormData({...formData, retail_price: e.target.value})}
                    placeholder="0.00"
                    className="pl-8"
                    data-testid="product-retail-input"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="wholesale_price">Wholesale Price</Label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    id="wholesale_price"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.wholesale_price}
                    onChange={(e) => setFormData({...formData, wholesale_price: e.target.value})}
                    placeholder="0.00"
                    className="pl-8"
                    data-testid="product-wholesale-input"
                  />
                </div>
              </div>
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="btn-primary" data-testid="save-product-btn">
                {editingProduct ? 'Update' : 'Create'} Product
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* AI Pricing Dialog */}
      <Dialog open={isPricingOpen} onOpenChange={setIsPricingOpen}>
        <DialogContent className="sm:max-w-lg border-2 border-[#1E3A5F]">
          <DialogHeader>
            <DialogTitle className="text-[#1E3A5F] flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-[#1E3A5F]" />
              AI Pricing Recommendation
            </DialogTitle>
            <DialogDescription>
              {pricingData?.product_name || 'Analyzing product costs and market factors...'}
            </DialogDescription>
          </DialogHeader>
          
          {pricingLoading ? (
            <div className="flex flex-col items-center justify-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1E3A5F] mb-4"></div>
              <p className="text-gray-600">Analyzing pricing factors...</p>
            </div>
          ) : pricingData ? (
            <div className="space-y-4">
              {/* Cost Summary */}
              <div className="grid grid-cols-3 gap-3">
                <div className="text-center p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500">Materials</p>
                  <p className="font-mono font-bold">${pricingData.material_cost.toFixed(2)}</p>
                </div>
                <div className="text-center p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500">Labor</p>
                  <p className="font-mono font-bold">${pricingData.labor_cost.toFixed(2)}</p>
                </div>
                <div className="text-center p-3 bg-[#1E3A5F] rounded-lg text-white">
                  <p className="text-xs opacity-80">Total Cost</p>
                  <p className="font-mono font-bold">${pricingData.total_cost.toFixed(2)}</p>
                </div>
              </div>

              {/* Recommended Prices */}
              <div className="border-2 border-[#1E3A5F] rounded-lg p-4 bg-[#FFEDDF]">
                <h4 className="font-semibold text-[#1E3A5F] mb-3">Recommended Prices</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Retail Price</p>
                    <p className="text-2xl font-bold text-[#1E3A5F]">
                      ${pricingData.recommended_retail_price.toFixed(2)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Wholesale Price</p>
                    <p className="text-2xl font-bold text-gray-700">
                      ${pricingData.recommended_wholesale_price.toFixed(2)}
                    </p>
                  </div>
                </div>
                <p className="text-sm text-gray-600 mt-2">
                  Profit Margin: <span className="font-semibold text-green-600">{pricingData.profit_margin}%</span>
                </p>
              </div>

              {/* Reasoning */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-semibold text-[#1E3A5F] mb-2">Pricing Strategy</h4>
                <p className="text-sm text-gray-700">{pricingData.reasoning}</p>
              </div>

              {/* Pricing Tiers */}
              <div>
                <h4 className="font-semibold text-[#1E3A5F] mb-3">Price Tiers</h4>
                <div className="space-y-2">
                  {pricingData.pricing_tiers?.map((tier) => (
                    <div 
                      key={tier.tier} 
                      className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50 cursor-pointer"
                      onClick={() => handleApplyPricing(tier.price || 0, (tier.price || 0) * 0.5)}
                    >
                      <div>
                        <span className="font-medium capitalize">{tier.tier?.replace('_', ' ') || 'Price'}</span>
                        <p className="text-sm text-gray-500">{tier.description}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-bold text-[#1E3A5F]">${(tier.price || 0).toFixed(2)}</span>
                        <ChevronRight className="w-4 h-4 text-gray-400" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setIsPricingOpen(false)}>
                  Close
                </Button>
                <Button 
                  className="btn-primary"
                  onClick={() => handleApplyPricing(pricingData.recommended_retail_price, pricingData.recommended_wholesale_price)}
                  data-testid="apply-pricing-btn"
                >
                  Apply Recommended Prices
                </Button>
              </DialogFooter>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Manage Categories Dialog */}
      <Dialog open={isCategoriesOpen} onOpenChange={setIsCategoriesOpen}>
        <DialogContent className="sm:max-w-md border-2 border-[#1E3A5F]">
          <DialogHeader>
            <DialogTitle className="text-[#1E3A5F] flex items-center gap-2">
              <Settings2 className="w-5 h-5" />
              Manage Categories
            </DialogTitle>
            <DialogDescription>
              Add or remove product categories
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Add new category */}
            <div className="flex gap-2">
              <Input
                placeholder="New category name"
                value={newCategoryInput}
                onChange={(e) => setNewCategoryInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAddNewCategoryFromDialog()}
                data-testid="new-category-input"
              />
              <Button onClick={handleAddNewCategoryFromDialog} className="btn-primary px-4" data-testid="add-category-btn">
                <Plus className="w-4 h-4" />
              </Button>
            </div>

            {/* Category list */}
            <div className="border rounded-lg divide-y max-h-64 overflow-y-auto">
              {categories.map((cat) => {
                const isInUse = products.some(p => p.category === cat);
                return (
                  <div key={cat} className="flex items-center justify-between p-3 hover:bg-gray-50">
                    <span className="capitalize font-medium">{cat}</span>
                    <div className="flex items-center gap-2">
                      {isInUse && (
                        <span className="text-xs text-gray-400">In use</span>
                      )}
                      <button
                        onClick={() => handleRemoveCategory(cat)}
                        disabled={isInUse}
                        className={`p-1 rounded ${isInUse ? 'text-gray-300 cursor-not-allowed' : 'text-red-500 hover:bg-red-50'}`}
                        title={isInUse ? 'Category is in use' : 'Remove category'}
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            <p className="text-xs text-gray-500">
              Categories in use by products cannot be removed.
            </p>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCategoriesOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Products;
